import { Rank, SubDepartment } from "@/modules/user/types/index.v2";
import { UUID } from "crypto";
import { ORGANIZATION_TIER_TYPES } from "@/modules/common/constants";
import { LeadTag } from "@/modules/lead-tags/dtos";
import { SavedFilterScreen } from "@/modules/common/types";
import { SavedFiltersMap } from "@/modules/common/types";
import { Partner, Referral } from "@/modules/leads/types";
import { LeadTypeForm } from "@/modules/lead-page/dtos/index-v2";
import { Listing } from "src/modules/listing-dashboard/dtos";
import { OverviewKey } from "@/modules/home/enums";

export interface BaseDTO {
  id: number;
  uid: UUID;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BasePaginationResponse<T> {
  page: number;
  pageSize: number;
  total: number;
  results: T[];
}

export interface YmirPaginationResponse<T> {
  pageSize: number;
  pages: number;
  count: number;
  data: T[];
}

export interface BasePaginationParams {
  page?: number;
  pageSize: number;
  search?: string;
  name?: string;
}

export interface ApiResponse<T> {
  data: T;
}

export interface MethodsOfContactFilters extends BasePaginationParams {
  withDeleted?: boolean;
  isDeleted?: boolean;
  sort?: string;
  filter?: string;
  search?: string;
  name?: string;
  pool?: boolean;
  list?: boolean;
  personal?: boolean;
  organizationUId?: string;
}

export interface MethodOfContact extends BaseDTO {
  name: string;
  pool: boolean;
  personal: boolean;
  list: boolean;
}

export type GetMethodsOfContactResponse =
  BasePaginationResponse<MethodOfContact>;

export interface LeadSource extends BaseDTO {
  name: string;
}

export interface CustomerEmails extends BaseDTO {
  name: string;
  organizationId: number;
  email: string;
  customerId: number;
  default: boolean;
}

export interface CustomerTelephones extends BaseDTO {
  name: string;
  organizationId: number;
  telephone: string;
  default: boolean;
  customerId: number;
}

export interface Customer extends BaseDTO {
  name: string;
  organizationId: number;
  bestTimeToCall: string | null;
  doNotCall: boolean;
  telephones: CustomerTelephones[];
  leads: Lead[];
  emails: CustomerEmails[];
  type: "single" | "bulk";
}

export interface UpdateCustomerRequest {
  name?: string;
  doNotCall?: boolean;
  bestTimeToCall?: string | null;
}

export interface UpdateJourneyRequest {
  dateOfLastContact?: string | Date | Date[] | undefined;
  rubbish?: boolean;
  rubbishReason?: string | null;
  rubbishComment?: string | null;
}

export interface AttachmentMetadata {
  duration?: number;
}

export interface UpdateJourneyStatusRequest {
  statusUId: UUID;
  comment: string;
  formData?: { value: string; fieldUId: UUID }[];
  trackUId?: string;
  attachments?: File[];
  attachmentMetadata?: AttachmentMetadata[];
  erealtyForm?: boolean;
  erealtyFormData?: any;
  createDeveloperTrackDTO?: CreateLeadTrackDTO[];
  salesForm?: boolean;
  saleDTO?: Record<string, any>;
  saleFiles?: File[];
  salePaymentFiles?: File[];
  salePaymentPlanFiles?: File[];
}

export interface CreateLeadTrackDTO {
  leadUId: string;
  developerUId?: string;
  developerUserUId?: string;
}

export interface FormFieldsResponse extends BaseDTO {
  name: string;
  fields: FieldsResponse[];
  actions: any[];
  statuses: StatusesResponse;
}

export interface FieldsResponse extends BaseDTO {
  field: Field;
  isHidden: boolean;
  isDisabled: boolean;
  useInAction: boolean;
  isMulti: boolean;
  required: boolean;
}

export interface Field extends BaseDTO {
  label: string | null;
  key: string | null;
  description: string | null;
  type: string;
  dropdowns: any[];
  isPhone: boolean;
  isEmail: boolean;
  defaultValue: string | null;
  customRegex: string | null;
  maxValue: number | null;
  minValue: number | null;
  maxDate: string | null;
  minDate: string | null;
  maxTime: string | null;
  minTime: string | null;
  dynamicFormUrl?: string | null;
  dynamicFormPath?: string | null;
  dynamicFormLabel?: string | null;
  dynamicFormValue?: string | null;
}

export interface UpdateEmails {
  email: string | null;
  default: boolean;
}

export interface AddTelephone {
  telephone: string;
  default: boolean;
}
export interface UpdateTelephone {
  telephone?: string;
  default?: boolean;
}
export interface DeleteTelephone {
  telephoneUID?: string;
}
export interface Ad extends BaseDTO {
  name: string;
  uid: UUID;
}

export interface Adset extends BaseDTO {
  name: string;
  uid: UUID;
}
export interface Campaign extends BaseDTO {
  name: string;
  uid: UUID;
}
export interface Location extends BaseDTO {
  name: string;
  locationType?: LocationType;
  compoundId?: number | null;
  isExpo?: boolean;
}

export interface FunnelStageMapping extends BaseDTO {
  id: number;
  order: number;
  funnelStage: {
    id: number;
    uid: string;
    name: string;
    activeIcon: string;
    inactiveIcon: string;
    organizationId: number;
    createdAt: string;
    updatedAt: string;
  };
}

export interface Funnel extends BaseDTO {
  name: string;
  defaultStatusId: number;
  stageMappings: FunnelStageMapping[];
  reassignmentDefaultStatusId?: number;
  poolReassignmentDefaultStatusId?: number;
}

export interface LeadType extends BaseDTO {
  name: string;
  form: LeadTypeForm;
  funnelId: number;
  managerReassignmentValidity: number;
  acceptListing: boolean;
  allowUpdate: boolean;
  allowReferrals: boolean;
  allowUserUpdates: boolean;
  createOwner: boolean;
  dynamic: boolean;
  funnel: Funnel;
  default: boolean;
  has_developer_tracks?: boolean;
  hasDeveloperTracks?: boolean;
  leadTypeCategoryId?: number | null;
}

export interface Lead extends BaseDTO {
  duplicate?: boolean;
  oldId?: number;
  leadTags?: LeadTag[];
  rubbish?: boolean;
  archived?: boolean;
  methodOfContact: MethodOfContact;
  dateOfLastRequest: string | null;
  leadSource: LeadSource;
  ad: Ad | null;
  adset: Adset | null;
  campaign: Campaign | null;
  location: Location | null;
  customer: Customer;
  leadType: LeadType;
  leadInfos?: {
    [key: string]: string | number | boolean;
  }[];
  dateOfLastContact: string;
  validityTime: string;
  poolValidityTime: string;
  parked: boolean;
  database: boolean;
  organization: Organization;
  launch?: {
    uid: string;
    deletedAt: null | string;
    createdAt: string;
    updatedAt: string;
    name: string;
    leadIcon?: {
      uid: string;
      deletedAt: null | string;
      createdAt: string;
      updatedAt: string;
      condition: {
        launches: string;
      };
      displayName: string;
      imageUrl: string;
    } | null;
  } | null;
  journeys?: Journey[];
  partner?: Partner | null;
  referral?: Referral[] | Referral;
  listings?: Listing[];
}

export interface UpdateLeadRequest {
  tagUIds?: string;
  removeLeadTagUId?: string;
  leadTypeUId?: UUID;
  leadSourceUId?: UUID;
  methodOfContactUId?: UUID;
  partnerUId?: UUID;
  dateOfLastRequest?: string | Date | Date[] | undefined;
  adUId?: UUID;
  adsetUId?: UUID;
  campaignUId?: UUID;
  locationUId?: UUID;
  archived?: boolean;
  rubbish?: boolean;
  rubbishReason?: string | null;
  rubbishComment?: string | null;
}

export interface Organization extends BaseDTO {
  name: string;

  fav?: {
    id: number;
    uid: UUID;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    link: string;
  };
  logo?: {
    id: number;
    uid: UUID;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    link: string;
  };
  tier?: {
    name: string;
    id: number;
    uid: UUID;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    type: string;
  };
}

export interface UserOrganization extends BaseDTO {
  userOrganizationsTierTypes?: (typeof ORGANIZATION_TIER_TYPES)[keyof typeof ORGANIZATION_TIER_TYPES][];
  organization: Organization;
  manager?: managedOrganization[];
  tags?: UserTag[];
}
export interface managedOrganization extends BaseDTO {
  name?: string;
  organization?: Organization;
}
export interface Funnel extends BaseDTO {
  name: string;
  defaultStatusId: number;
  defaultStatus: string;
  organization: Organization;
  reassignmentDefaultStatusId?: number;
  poolReassignmentDefaultStatusId?: number;
}

export interface StatusCategory extends BaseDTO {
  name: string;
  pool: boolean;
  leadJourneyFunnelId: number;
  organization: Organization;
  stageId?: number;
}

export interface Status extends BaseDTO {
  name: string;
  statusCategoryId: number;
  nextStatusCategoryId: number;
  color: string;
  oldColor: string;
  pool: boolean;
  negative: boolean;
  statusType: StatusType | null;
  validityTime: number;
  poolValidityTime: number;
  formId: number | null;
  erealtyForm: boolean;
  createDeveloperTrack?: boolean;
  statusCategory: {
    uid: string;
    deletedAt: string | null;
    createdAt: string;
    updatedAt: string | null;
    name: string;
    stageId?: number;
  };
  nextStatusCategory: {
    uid: UUID;
    deletedAt: string | null;
    createdAt: string;
    updatedAt: string | null;
    name: string;
  };
}

export interface User {
  id: number;
  uid?: UUID;
  deletedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  name: string | null;
  email: string | null;
  phoneNumber?: string | null;
  color: string | null;
  profilePictureUrl?: string | null;
  rank?: Rank;
  rankId?: number;
  organization?: {
    name: string;
  };
  status?: string | null;
}

export interface MergeRecord extends BaseDTO {
  oldJourneyId: number;
  newJourneyId: number;
  userId: number;
}

export interface Journey extends BaseDTO {
  nanoId: string;
  dateOfLastContact: string | null;
  customer: Customer;
  lead: Lead;
  rubbish?: boolean;
  mergeRecordAsOld?: MergeRecord;
  mergeRecordsAsNew?: MergeRecord[];
  status: Status;
  user: User;
  organization: Organization;
  propertyId?: number | null;
  leadIcons:
    | {
        uid: string;
        deletedAt: null | string;
        createdAt: string;
        updatedAt: string;
        condition: {
          [key: string]: string | number | boolean;
        };
        displayName: string;
        imageUrl: string;
      }[]
    | [];
  previousStatus?: Partial<Status>;
  previousUser?: Partial<User>;
  isOnCall?: boolean;
}

export interface MethodsOfContactResponse {
  data: {
    total: number;
    pageSize: number;
    page: number;
    results: MethodOfContact[];
  };
}

export interface LeadTypesResponse {
  data: {
    total: number;
    pageSize: number;
    page: number;
    results: LeadType[];
  };
}

export interface LeadSourceResponse {
  data: {
    total: number;
    pageSize: number;
    page: number;
    results: LeadSource[];
  };
}

export interface StatusesResponse {
  data: {
    total: number;
    pageSize: number;
    page: number;
    results: Status[];
  };
}

export type UsersList = {
  name: string;
  uid: UUID;
  color?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  cognitoId: string;
  preferredUsername: string | null;
  email: string | null;
  phoneNumber: string | null;
  banned: boolean;
  teamSize: number;
  status: UserStatus;
  domain: {
    name: string;
    uid: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    userPoolId: string;
  };
  job: UserJob | null;
  customGroups: [];
  identityProviders: [];
  organization: {
    name: string;
    uid: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  rank: {
    uid: string;
    id: number;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    title: string;
    value: number;
  };
  configuration: UserConfigurations;
  rankId: number;
  tags: UserTag[];
  subDepartment: SubDepartment | null;
  supervisor: User | null;
};

export interface UserTag extends BaseDTO {
  name: string;
  category: {
    duplicate: boolean;
    color?: string;
  };
}

export interface UserJob extends BaseDTO {
  name: string;
  jobGroups: JobGroup[];
}

export interface JobGroup extends BaseDTO {
  jobId: number;
  groupId: number;
}
export interface MappedUserData {
  id: UUID;
  name: string;
  color: string;
  createdAt: string;
  updatedAt: string;
  configurations: UserConfigurations;
  banned: boolean;
  rankUId: number;
  subDepartment: SubDepartment | null;
  email: string | null;
  phoneNumber: string | null;
  preferredUsername: string | null;
  teamSize: number;
}

export interface AutodialerState {
  active: boolean;
  appliedAt: string | null;
}

export interface UserConfigurations {
  isAvailable: boolean;
  reassignmentEligibility: boolean;
  delegationWeights: number;
  duplicatesDelegationFlag: boolean;
  duplicateStopperFlag: boolean;
  userStatus: UserStatus;
  stopDelegationFlag: boolean;
  autoAssignmentWeight: number;
  lastActivityDate: string;
  autodialerState: AutodialerState | null;
  rotationManager: boolean;
}

export enum UserStatus {
  Active = "active",
  Banned = "banned",
  Blocked = "blocked",
  Fresh = "fresh",
}

export type GetUsersListResponse = {
  data: {
    page: number;
    pageSize: number;
    total: number;
    results: UsersList[];
  };
};

export interface StatusCategoriesResponse {
  data: {
    total: number;
    pageSize: number;
    page: number;
    results: StatusCategory[];
  };
}

export interface Compound extends BaseDTO {
  name: string;
  area?: Area;
  developer: Developer;
}

export interface Area extends BaseDTO {
  name: string;
}

export interface PropertyType {
  uid: string;
  name: string;
  deletedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface Developer extends BaseDTO {
  name: string;
  logoPath?: string;
}

export type LocationType = "Compound" | "Area" | "Developer" | "Custom";

export interface LocationsResponse {
  data: {
    total: number;
    pageSize: number;
    page: number;
    results: {
      uid: string;
      name: string;
      parentLocationId: any;
      locationType: LocationType;
      area: Area;
      compound: Compound;
      developer: Developer;
      childLocationsUid?: string[];
      metadata?: {
        startDate: string;
        endDate: string;
      };
    }[];
  };
}

export interface CmsData {
  uid: UUID;
  organization: {
    name: string;
    uid: string;
  };
  name: string;
}

export interface CmsDataResponse {
  data: {
    total: number;
    pageSize: number;
    page: number;
    results: CmsData[];
  };
}

export interface MoveJourneysRequest {
  statusUId: UUID;
  userUId: UUID;
  journeyUIds: UUID[];
  removePersonal: boolean;
}

export interface MarkAsRubbishRequest {
  journeyUIds: UUID[];
  rubbishReason?: string | null;
  rubbishComment?: string | null;
}

export interface UserQueryParams {
  page?: number;
  pageSize?: number;
  organizationUIds?: string;
  sort?: string;
  filter?: string;
  search?: string;
  withDeleted?: boolean;
  isDeleted?: boolean;
  name?: string;
  preferredUsername?: string;
  email?: string;
  phoneNumber?: string;
  jobId?: number;
  customGroupId?: number;
  domainId?: number;
  organizationUId?: string;
  statuses?: UserStatus[];
  rankIds?: number[];
  teamUIds?: string[];
  inATeam?: boolean;
  savedUIds?: string;
  supervisorUIds?: string;
  minRankValue?: number;
  maxRankValue?: number;
  userUid?: UUID;
  isInActiveOnly?: boolean;
  isActiveOnly?: boolean;
  subDepartmentUId?: string;
  skipHierarchy?: boolean;
  assignToUsersQuery?: boolean;
  includeOnlyExpoUsers?: boolean;
  excludeUsersWithBlockAllLeads?: boolean;
}
export interface PropertyTypeResponse {
  data: {
    id: number | null;
    oldId?: null | number | string;
    migrationOrgId?: null | number | string;
    uid: string;
    deletedAt: null | string;
    createdAt: string;
    updatedAt: string;
    name: string;
  }[];
}

export interface UpdateUserConfig {
  reassignmentEligibility?: boolean;
  delegationWeights?: number;
  duplicatesDelegationFlag?: boolean;
  isAvailable?: boolean;
  userStatus?: UserStatus;
  stopDelegationFlag?: boolean;
  autoAssignmentWeight?: number;
  duplicateStopperFlag?: boolean;
  blockFreshLeads?: boolean;
  blockAllLeads?: boolean;
  rotationManager?: boolean;
  // lastEditToBlockBy?: User;
  // lastEditToBlockAt?: string;
}

export enum StatusType {
  Meeting = "Meeting",
  SALE_FIRST_ACTION = "First Serious Action",
  SALE_CONTRACTED = "Contracted",
  LISTING = "Listing",
  NO_ANSWER = "No Answer",
}

export interface getJourneyStatusesResponse {
  name: string;
  color: string | null;
  oldColor: string | null;
  pool: boolean;
  negative: boolean;
  statusType: StatusType | null;
  validityTime: number;
  poolValidityTime: number;
  notifySupervisor: boolean;
  notifySalesManager: boolean;
  customerNotificationEnabled: boolean;
  important: string | boolean | null;
  alertsSchedule: string | null;
  statusCategory: StatusCategory;
  uid: UUID;
  deletedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  form: null | {
    name: string;
    uid: string;
  };
}

export interface UserAvaililityResponse {
  data: {
    id: number;
    oldId: number | null;
    migrationOrgId: number | null;
    uid: UUID;
    deletedAt: string | null;
    createdAt: string;
    updatedAt: string;
    userId: number;
    reassignmentEligibility: boolean;
    delegationWeights: string;
    duplicatesDelegationFlag: boolean;
    duplicateStopperFlag: boolean;
    isAvailable: boolean;
    userStatus: UserStatus;
    stopDelegationFlag: boolean;
    autoAssignmentWeight: string;
    autodialerState: AutodialerState | null;
    user: User;
  };
}

export interface LookupsResponse
  extends BasePaginationResponse<LookupsOptions> {}

export interface FiltersMultiResponse {
  ids: number[];
  name: string;
  color?: string;
}

export interface FiltersResponse {
  id: number;
  name: string;
  uid?: string;
  color?: string;
  enablePropertyManagement?: boolean;
}

export interface ReplicatedFiltersResponse {
  uid: string;
  name: string;
  color?: string;
}

export interface LookupsOptions {
  id?: number;
  uid: string;
  name: string;
  color?: string;
  oldColor?: string;
  formUid?: string;
  email?: string;
  enablePropertyForm?: boolean;
}

export interface LookupsParams extends BasePaginationParams {
  organizationUIds?: string;
  savedNames?: string;
  savedUIds?: string;
  includeOnlyExpoUsers?: boolean;
}

export interface StatusesLookupsParams extends LookupsParams {
  overviewEnum?: OverviewKey;
}

export interface UserSavedFiltersDto extends BaseDTO {
  name: string;
  filters: SavedFiltersMap[SavedFilterScreen];
}

export interface AddBulkTagsRequest {
  leadUIds: UUID[];
  tagUIds: UUID[];
}

export interface QueryPageComponentParams {
  page?: string;
  trackTypeId?: number;
}

export interface PageComponentDto {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}
